/**
 * Cuadre de Caja Resumen — migración web de ares7075.
 *
 * Cobrado por sucursal en el rango: total general, capital, interés y
 * mora de los pagos, más iniciales y ventas al contado de facturas del
 * período. Sin el join a factmot del escritorio, que duplicaba los
 * pagos de préstamos con más de una factura e inflaba los totales.
 * Exporta a Excel e imprime.
 */
import React, { useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales });
}

const moneyCol = (field, headerName, width = 130) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function CuadreCajaResumen({ embedded = false, onExit } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(hoyISO());
  const [fecha2, setFecha2] = useState(hoyISO());

  const [sucursales, setSucursales] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/cuadrecaja_resumen", {
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) {
        setSucursales(data.sucursales || []);
        setTotales(data.totales || null);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el resumen", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo = `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`;

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Cuadre de Caja Resumen"],
      [subtitulo],
      [],
      ["Sucursal", "Total General", "Capital", "Interés", "Mora", "Iniciales", "Contado"],
      ...sucursales.map((s) => [
        s.sucursal,
        s.totalgeneral,
        s.totalcapital,
        s.totalinteres,
        s.totalmora,
        s.totalinicial,
        s.totalcontado,
      ]),
      [],
      [
        "TOTAL",
        totales?.totalgeneral || 0,
        totales?.totalcapital || 0,
        totales?.totalinteres || 0,
        totales?.totalmora || 0,
        totales?.totalinicial || 0,
        totales?.totalcontado || 0,
      ],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Cuadre");
    XLSX.writeFile(libro, `CuadreCajaResumen_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirListado(
      "Cuadre de Caja Resumen",
      subtitulo,
      ["Sucursal", "Total General", "Capital", "Interés", "Mora", "Iniciales", "Contado"],
      sucursales.map((s) => [
        s.sucursal,
        fmtMoney(s.totalgeneral),
        fmtMoney(s.totalcapital),
        fmtMoney(s.totalinteres),
        fmtMoney(s.totalmora),
        fmtMoney(s.totalinicial),
        fmtMoney(s.totalcontado),
      ]),
      [
        "TOTAL",
        fmtMoney(totales?.totalgeneral),
        fmtMoney(totales?.totalcapital),
        fmtMoney(totales?.totalinteres),
        fmtMoney(totales?.totalmora),
        fmtMoney(totales?.totalinicial),
        fmtMoney(totales?.totalcontado),
      ]
    );
  };

  const columnas = [
    { field: "sucursal", headerName: "Sucursal", flex: 1, minWidth: 160 },
    moneyCol("totalgeneral", "Total General"),
    moneyCol("totalcapital", "Capital"),
    moneyCol("totalinteres", "Interés"),
    moneyCol("totalmora", "Mora"),
    moneyCol("totalinicial", "Iniciales"),
    moneyCol("totalcontado", "Contado"),
  ];

  const hayDatos = sucursales.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cuadre de Caja Resumen"
        subtitle="Cobrado por sucursal: capital, interés, mora, iniciales y contado"
        cta={
          embedded ? (
            <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          ) : undefined
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="TOTAL GENERAL" value={`RD$ ${fmtMoney(totales?.totalgeneral)}`} />
            <ResumenChip
              label="CAPITAL"
              value={`RD$ ${fmtMoney(totales?.totalcapital)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="INTERÉS"
              value={`RD$ ${fmtMoney(totales?.totalinteres)}`}
              color="#F9A825"
            />
            <ResumenChip
              label="MORA"
              value={`RD$ ${fmtMoney(totales?.totalmora)}`}
              color="#C62828"
            />
            <ResumenChip
              label="INICIALES"
              value={`RD$ ${fmtMoney(totales?.totalinicial)}`}
              color="#EF6C00"
            />
            <ResumenChip
              label="CONTADO"
              value={`RD$ ${fmtMoney(totales?.totalcontado)}`}
              color="#1565C0"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={sucursales}
              columns={columnas}
              getRowId={(r) => r.sucursal}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
